// AgreementTotalsEdit.tsx
import { Box, Grid, MenuItem, useMediaQuery, useTheme } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { useContactsItems } from 'api/aws/contacts/useContactItems'
import { useUserItems } from 'api/aws/users/useUserItems'
import { dynamicGridSizes } from 'components/ContractDialog/style'
import ExistingDeal from 'components/CreateDeal/ExistingDealSelect'
import AuthorizedRecipientAutocomplete from 'components/PandaDoc/AuthorizedRecipientAutocomplete'
import ContactRecipientAutocomplete from 'components/PandaDoc/ContactRecipientAutocomplete'
import StyledInputAdornment from 'components/ui/styled-input-adornment'
import dayjs from 'dayjs'
import React from 'react'
import { DealFieldGroup, DealFieldLabel, DealInfoContent } from 'styles/utilities/view-agreement'
import { NewDeal, OrganizationTableItem, Product, Totals, ValidationErrors } from 'types'
import '../deals.css'
import NumberOfPaymentsField from '../fields/NumberOfPaymentsField'
import { DealOnlyTextFields } from '../style'

type Props = {
  deal?: NewDeal
  organization?: OrganizationTableItem
  productRows: Product[]
  totals: Totals
  validationErrors: ValidationErrors
  isDraft?: boolean

  // generic field updater (for simple top-level fields)
  onChange: (field: keyof NewDeal, value: any) => void

  onChangeContactRecipients: (list: any[]) => void
  onChangeAuthorizedRecipients: (list: any[]) => void
}

const numberOfPaymentOptions = ['1', '2', '3', '4', '5', '6']
const displayName = (r: any) =>
  r?.name ||
  [r?.first_name ?? r?.firstName, r?.last_name ?? r?.lastName].filter(Boolean).join(' ') ||
  r?.email ||
  'Unknown'
// --- Helpers ---
const isCurrencyDraft = (s: string) => /^(\d+(\.\d{0,2})?)?$/.test(s) // allows "", "1", "1.", "1.2", "1.23"

const AgreementTotalsEdit: React.FC<Props> = ({
  deal,
  organization,
  productRows,
  totals,
  validationErrors,
  isDraft = false,
  onChange,
  onChangeContactRecipients,
  onChangeAuthorizedRecipients,
}) => {
  const theme = useTheme()
  const isCSA = deal?.type && ['CSA', 'RCSA'].includes(deal.type)
  const isTallScreen = useMediaQuery('(min-height: 1200px)')
  const gridSizes = dynamicGridSizes(isTallScreen)
  const { data: contactOptions, isLoading: isContactsLoading } = useContactsItems()
  const { data: authorizedOptions, isLoading: isUsersLoading } = useUserItems()
  return (
    <DealInfoContent container>
      {/* Legal Entity */}
      <Grid item xs={12}>
        <DealFieldGroup>
          <DealFieldLabel>Legal Entity Name</DealFieldLabel>
          <DealOnlyTextFields
            fullWidth
            size="small"
            value={
              organization?.dba && organization?.dba !== ''
                ? organization?.dba
                : deal?.dba && deal?.dba !== ''
                ? deal?.dba
                : organization?.name ?? '–'
            }
            onChange={e => onChange('dba', e.target.value)}
          />
        </DealFieldGroup>
      </Grid>

      {/* Deal Type */}
      <Grid item xs={12} md={isCSA ? 6 : 12}>
        <DealFieldGroup>
          <DealFieldLabel>Deal Type</DealFieldLabel>
          <DealOnlyTextFields
            select
            fullWidth
            size="small"
            value={deal?.type ?? ''}
            onChange={e => onChange('type', e.target.value)}
            error={Boolean((validationErrors as any).type)}
            helperText={(validationErrors as any).type ? 'Deal type is required' : ''}
          >
            {['MSA', 'CSA', 'IO'].map(opt => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </DealOnlyTextFields>
        </DealFieldGroup>
      </Grid>

      {/* Payments (CSA-only) */}
      {isCSA && <NumberOfPaymentsField deal={deal} totals={totals} onChange={onChange} />}

      {/* Dates */}
      <Grid item xs={12} md={6}>
        <DealFieldLabel error={validationErrors.startDate}>Start Date</DealFieldLabel>
        <DatePicker
          value={deal?.startDate ? dayjs(deal.startDate) : null}
          onChange={val => onChange('startDate', val?.toDate() ?? null)}
          slotProps={{
            textField: { fullWidth: true, error: validationErrors.startDate },
          }}
          sx={{ backgroundColor: 'background.defaultdim' }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <DealFieldLabel error={validationErrors.endDate}>End Date</DealFieldLabel>
        <DatePicker
          value={deal?.endDate ? dayjs(deal.endDate) : null}
          onChange={val => onChange('endDate', val?.toDate() ?? null)}
          slotProps={{
            textField: { fullWidth: true, error: validationErrors.endDate },
          }}
          sx={{ backgroundColor: 'background.defaultdim' }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <DealFieldLabel error={validationErrors.dependenciesDueDate}>Dependencies Due</DealFieldLabel>
        <DatePicker
          value={deal?.dependenciesDueDate ? dayjs(deal.dependenciesDueDate) : null}
          onChange={val => onChange('dependenciesDueDate', val?.toDate() ?? null)}
          slotProps={{
            textField: {
              fullWidth: true,
              error: validationErrors.dependenciesDueDate,
            },
          }}
          sx={{ backgroundColor: 'background.defaultdim' }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <DealFieldLabel error={validationErrors.targetLaunchDate}>Target Launch</DealFieldLabel>
        <DatePicker
          value={deal?.targetLaunchDate ? dayjs(deal.targetLaunchDate) : null}
          onChange={val => onChange('targetLaunchDate', val?.toDate() ?? null)}
          slotProps={{
            textField: {
              fullWidth: true,
              error: validationErrors.targetLaunchDate,
            },
          }}
          sx={{ backgroundColor: 'background.defaultdim' }}
        />
      </Grid>

      {/* Contact Recipient (CLIENT) */}
      <Grid item {...gridSizes.contactRecipient}>
        <Box width="100%">
          <DealFieldLabel error={validationErrors.contactRecipient}>Contact Recipient</DealFieldLabel>
          <ContactRecipientAutocomplete
            recipients={contactOptions}
            value={deal?.recipients?.contactRecipient ?? []}
            label=""
            onChange={onChangeContactRecipients}
            error={validationErrors.contactRecipient}
            isDraft={deal?.isDraft}
            isCurrentRecipient={false}
          />
        </Box>
      </Grid>

      {/* Authorized Recipient (GRO) */}
      <Grid item {...gridSizes.authorizedRecipient}>
        <Box width="100%">
          <DealFieldLabel error={validationErrors.authorizedRecipient}>GRO Authorized Recipient</DealFieldLabel>
          <AuthorizedRecipientAutocomplete
            recipients={authorizedOptions}
            value={deal?.recipients?.authorizedRecipient ?? []}
            label=""
            onChange={onChangeAuthorizedRecipients}
            error={validationErrors.authorizedRecipient}
            isDraft={deal?.isDraft}
            isCurrentRecipient={false}
          />
        </Box>
      </Grid>
      {!isCSA && (
        <>
          <Grid item {...gridSizes.noSignature}>
            <Box width="100%">
              <ExistingDeal
                noSignature={deal?.noSignature ?? false}
                newDeal={deal}
                organization={organization}
                handleChange={onChange}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={isCSA ? 6 : 12}>
            <DealFieldGroup>
              <DealFieldLabel className="accent">Implementation Fee</DealFieldLabel>
              <DealOnlyTextFields
                className="input-imp-fee"
                fullWidth
                size="small"
                name="implementationFee"
                value={deal?.implementationFee ?? ''}
                onChange={e => {
                  const next = e.target.value
                  if (next === '' || isCurrencyDraft(next)) {
                    onChange('implementationFee', next)
                  }
                }}
                onBlur={() => {
                  const raw = deal?.implementationFee ?? ''
                  if (raw === '' || raw === '.') {
                    onChange('implementationFee', '')
                    return
                  }
                  const n = Number(raw)
                  onChange('implementationFee', Number.isFinite(n) ? n.toFixed(2) : '')
                }}
                type="text"
                inputMode="decimal"
                InputProps={{ startAdornment: <StyledInputAdornment /> }}
                inputProps={{ inputMode: 'decimal' }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: 14,
                  },
                }}
                disabled={!deal?.isDraft}
              />
            </DealFieldGroup>
          </Grid>
        </>
      )}
    </DealInfoContent>
  )
}

export default AgreementTotalsEdit
